// src/features/passwords/hooks/usePasswordDerived.js
import { useMemo } from 'react';
import { sortBySubscribed, sortByOnboarded } from '../utils/sorters';

/**
 * Derived data pipeline:
 *  - Enrich passwords with user/port info
 *  - Filter by search term
 *  - Group by user_code
 *  - Sort by subscribed/onboarded if requested
 *  - Paginate results (done by the caller)
 */
export default function usePasswordDerived({
  passwords,
  users,
  ports,
  searchTerm,
  sortField,
  sortDirection,
  currentPage,
  itemsPerPage,
}) {
  // ---- helpers ----
  const getPhoneNumber = (userCode, email) => {
    if (!userCode || !email) return '';
    const uc = String(userCode).trim().toLowerCase();
    const em = String(email).trim().toLowerCase();
    const p = ports.find(
      (pt) =>
        (pt.user_code || '').trim().toLowerCase() === uc &&
        (pt.user_email || '').trim().toLowerCase() === em
    );
    return p?.assigned_number || '';
  };

  const firstPortForUser = (userCode) => {
    const uc = String(userCode || '').trim().toLowerCase();
    return ports.find(
      (pt) => (pt.user_code || '').trim().toLowerCase() === uc
    );
  };

  // ----------------- Enrich individual password rows -----------------
  const enrichedPasswords = useMemo(() => {
    return passwords.map((pw) => {
      const perUser = users[pw.user_code] || {};
      const base = perUser.base || {};
      const emailData = perUser[pw.email] || {};

      const fullName =
        emailData.email_specific_name ||
        base.email_specific_name ||
        base.full_name ||
        '';

      const address =
        emailData.email_specific_address ||
        base.email_specific_address ||
        base.address ||
        '';

      // accept both DOB/dob/email_specific_dob
      const dob =
        emailData.email_specific_dob ||
        base.email_specific_dob ||
        base.dob ||
        base.DOB ||
        '';

      const notes = base.notes || '';
      const phone_number = getPhoneNumber(pw.user_code, pw.email);
      const slack_id = base.slack_user_id || '';

      return {
        ...pw,
        full_name: fullName,
        address,
        dob,
        notes,
        phone_number,
        slack_id,
        user_managed_numbers: pw.user_managed_numbers || [], // User-managed phone numbers (not on system)
        email_specific_name: emailData.email_specific_name,
        email_specific_address: emailData.email_specific_address,
        email_specific_dob: emailData.email_specific_dob,
      };
    });
    // ports intentionally excluded from deps for stability; we only use it via getPhoneNumber
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwords, users]);

  // ----------------- Filter -----------------
  const filterPasswords = (list, term) => {
    const lower = term.toLowerCase().trim();
    if (!lower) return list;
    return list.filter((pw) => {
      const codeMatch = pw.user_code?.toLowerCase().includes(lower);
      const nameMatch = pw.full_name?.toLowerCase().includes(lower);
      const emailMatch = pw.email?.toLowerCase().includes(lower);
      const phoneMatch = pw.phone_number?.toLowerCase().includes(lower);
      const slackIdMatch = pw.slack_id?.toLowerCase().includes(lower);
      return codeMatch || nameMatch || emailMatch || phoneMatch || slackIdMatch;
    });
  };

  const filteredPasswords = useMemo(
    () => filterPasswords(enrichedPasswords, searchTerm),
    [enrichedPasswords, searchTerm]
  );

  // ----------------- Group by user_code -----------------
  const groupedPasswords = useMemo(() => {
    const grouped = filteredPasswords.reduce((acc, item) => {
      (acc[item.user_code] ||= []).push(item);
      return acc;
    }, {});
    
    // Sort each user's passwords alphabetically by email
    Object.keys(grouped).forEach(userCode => {
      grouped[userCode].sort((a, b) => {
        const emailA = (a.email || '').toLowerCase();
        const emailB = (b.email || '').toLowerCase();
        return emailA.localeCompare(emailB);
      });
    });
    
    return grouped;
  }, [filteredPasswords]);

  // ----------------- Build parent rows -----------------
  const userCodeMainEntries = useMemo(() => {
    return Object.entries(groupedPasswords).map(([userCode, entries]) => {
      const perUser = users[userCode] || {};
      const base = perUser.base || {};
      const mainEntry = entries.find((e) => e.main) || entries[0];

      const port = firstPortForUser(userCode);

      // robust fallbacks
      const fullName =
        base.email_specific_name ||
        base.full_name ||
        mainEntry?.full_name ||
        userCode;

      const slackId =
        base.slack_user_id ||
        port?.slack_user_id ||
        '';

      const referral =
        base.referral ||
        base.ref || // tolerate alternate field name if present
        '';

      const crm_password =
        base.crm_password ||
        base.crmPassword ||
        base['@password'] || // in case the backend sends this literal key
        '';

      const dob = base.dob || base.DOB || base.email_specific_dob ||
        entries.find(e => e.dob)?.dob || '';
      const address = base.address || '';
      const notes = base.notes || '';

      const subscribed = Boolean(base.subscribed);
      // subscription_status comes from the password rows (stitched by backend), not the users map
      const subscription_status = mainEntry?.subscription_status
        || entries.find(e => e.subscription_status)?.subscription_status
        || null;
      const onboarded = Boolean(base.onboarded);

      const multilogin = Object.prototype.hasOwnProperty.call(base, 'multilogin')
        ? Boolean(base.multilogin)
        : undefined;

      // Count ports (numbers) associated with this user code
      const uc = String(userCode || '').trim().toLowerCase();
      const portsForUser = ports.filter(
        (pt) => (pt.user_code || '').trim().toLowerCase() === uc
      );
      const numbersCount = portsForUser.length;

      // Unique email count (same email on multiple ports counts as one)
      const uniqueEmails = new Set(
        entries.map((e) => (e.email || '').trim().toLowerCase()).filter(Boolean)
      );
      const emailsCount = uniqueEmails.size;

      return {
        userCode,
        mainEntry,
        totalEntries: entries.length,
        emailsCount, // unique emails only
        numbersCount, // ✅ NEW: count of ports/numbers for this user
        fullName,
        slackId,
        referral,
        crm_password,
        dob,
        address,
        notes,
        subscribed,
        subscription_status,
        onboarded,
        multilogin,
        allEntries: entries,
      };
    });
  }, [groupedPasswords, users, ports]);

  // ----------------- Sort -----------------
  const sortedUserCodeMainEntries = useMemo(() => {
    let sorted = [...userCodeMainEntries];
    if (sortField === 'subscribed') {
      sorted = sortBySubscribed(sorted, sortDirection);
    } else if (sortField === 'onboarded') {
      sorted = sortByOnboarded(sorted, sortDirection);
    }
    return sorted;
  }, [userCodeMainEntries, sortField, sortDirection]);

  // ----------------- Pagination (kept here for completeness; caller also paginates) -----------------
  const paginatedEntries = useMemo(() => {
    return sortedUserCodeMainEntries.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [sortedUserCodeMainEntries, currentPage, itemsPerPage]);

  return {
    enrichedPasswords,
    groupedPasswords,
    userCodeMainEntries,
    sortedUserCodeMainEntries,
    paginatedEntries,
  };
}
